/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { Shield, AlertTriangle, ChevronDown, ChevronUp, Network, Brain, Coins } from 'lucide-react';

interface FAQItemProps {
    question: string;
    answer: React.ReactNode;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentId = `faq-content-${question.replace(/\s+/g, '-').toLowerCase().slice(0, 20)}`;

    return (
        <div className="border-b border-stone-200 last:border-0">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full py-4 flex items-center justify-between text-left group hover:bg-stone-50 transition-colors px-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-nobel-gold/20"
                aria-expanded={isOpen}
                aria-controls={contentId}
            >
                <span className="font-serif font-bold text-lg text-stone-800 group-hover:text-nobel-gold transition-colors">{question}</span>
                {isOpen ? <ChevronUp className="text-stone-400" /> : <ChevronDown className="text-stone-400" />}
            </button>
            {isOpen && (
                <div 
                    id={contentId} 
                    className="pb-6 px-2 text-stone-600 leading-relaxed text-sm md:text-base animate-fade-in"
                >
                    {answer}
                </div>
            )}
        </div>
    );
};

export const FAQ: React.FC = () => {
  // Safely access env var with optional chaining and fallback
  const getEnv = (key: string) => {
      try {
        // @ts-ignore
        return (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env[key] : undefined;
      } catch (e) {
        return undefined;
      }
  };
  
  const rewardsPool = getEnv('VITE_PUBLIC_REWARDS_POOL') || 'EbDPyHifxX45CS6PA9msSM7utJa1DiHsDWp5zP6htf11';

  return (
    <section id="faq" className="py-24 bg-white border-t border-stone-200">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-16">
            <div className="inline-block mb-3 text-xs font-bold tracking-widest text-stone-500 uppercase">Support & Knowledge</div>
            <h2 className="font-serif text-4xl text-stone-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-stone-500">Cross-chain operations, token security, and launch details.</p>
        </div>
        
        <div className="grid grid-cols-1 gap-12">
            
            {/* Multi-Chain Platform */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
                <h3 className="font-serif text-2xl mb-6 text-stone-900 flex items-center gap-2">
                    <Network size={24} className="text-stone-400" /> Multi-Chain Platform
                </h3>
                <div className="space-y-2">
                    <FAQItem 
                        question="Which networks does The Foundry support?" 
                        answer={
                            <span>
                                We currently support <strong>Solana (SOL)</strong> and <strong>Sui (SUI)</strong>. You can switch between networks using the ecosystem selector in the navigation bar. Each network has its own distinct token standards (SPL for Solana, Sui Coin for Sui).
                            </span>
                        }
                    />
                    <FAQItem 
                        question="How do I switch between Solana and Sui?" 
                        answer="Use the 'Networks' section on the homepage or the wallet connection modal. When you select a network card (e.g., clicking on the Solana card), the entire application context updates to display markets, pools, and tools relevant to that specific blockchain."
                    />
                    <FAQItem 
                        question="Can I bridge assets directly here?" 
                        answer="Not yet. While you can view and trade assets on both chains, cross-chain bridging (moving funds from SOL to SUI directly) requires external bridges like Wormhole or Portal. We plan to integrate a native bridge in Q4 2024."
                    />
                </div>
            </div>

            {/* AI & Economics */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
                <h3 className="font-serif text-2xl mb-6 text-stone-900 flex items-center gap-2">
                    <Brain size={24} className="text-stone-400" /> Neural Engine
                </h3>
                <div className="space-y-2">
                    <FAQItem 
                        question="What is Swarm Intelligence?" 
                        answer="Swarm Intelligence describes our decentralized analysis architecture. Instead of relying on a single algorithm, our Neural Engine runs hundreds of independent analysis nodes that vote on market conditions. A 'Signal' is only generated when over 80% of these nodes reach consensus."
                    />
                    <FAQItem 
                        question="How trustworthy is the AI Score?" 
                        answer="The AI Score (0-100) is a probabilistic metric derived from on-chain data, sentiment analysis, and smart money movement. While highly accurate at detecting trends, it is not financial advice. Scores > 80 indicate strong momentum and lower immediate risk."
                    />
                    <FAQItem 
                        question="What is the Smart Money Tracker?" 
                        answer="This feature identifies wallets that have historically high win rates (>60% PnL). By tracking these 'Smart Money' addresses in real-time, the platform can alert you when they are accumulating specific tokens before the general market notices."
                    />
                </div>
            </div>

            {/* Token Launch */}
            <div className="bg-stone-50 p-8 rounded-2xl border border-stone-200">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-stone-900 text-white rounded-lg">
                        <Shield size={24} />
                    </div>
                    <h3 className="font-serif text-2xl text-stone-900">Launch & Security</h3>
                </div>

                <div className="space-y-2">
                    <FAQItem 
                        question="What are the costs to deploy?" 
                        answer="Deployment fees vary by network. On Solana, it costs approximately 0.1 SOL (+ 0.05 SOL for Token-2022 extensions). On Sui, the base fee is around 5 SUI. These fees cover network gas and protocol service charges."
                    />
                    <FAQItem 
                        question="What is 'Revoke Mint Authority'?" 
                        answer="Revoking mint authority means you permanently disable the ability to create more tokens. This makes the supply 'Fixed' or 'Capped'. Investors generally prefer this as it prevents inflation or rug-pulls via unlimited minting."
                    />
                    <FAQItem 
                        question="Is liquidity automatically locked?" 
                        answer="No, liquidity locking is a separate step after minting. Once you create your liquidity pool (e.g., on Raydium for SOL or Cetus for SUI), you can use our 'Lock Liquidity' tool to secure the LP tokens for a set duration."
                    />
                    <FAQItem
                        question="What is the Rewards Pool?"
                        answer={
                            <span>
                                The <strong>Public Rewards Pool</strong> incentivizes active participation. A portion of platform fees is directed to this pool (Address: <code>{rewardsPool}</code>) and distributed to top traders and creators on the leaderboard weekly.
                            </span>
                        }
                    />
                </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-200">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-stone-200 shadow-sm">
                    <AlertTriangle className="text-amber-600 shrink-0 mt-1" size={24} />
                    <div>
                        <h4 className="font-bold text-stone-900 mb-2 text-sm uppercase tracking-wide">Disclaimer</h4>
                        <p className="text-xs text-stone-500 leading-relaxed mb-2">
                            The Foundry is a decentralized tool. We do not control the tokens created by users. Always Do Your Own Research (DYOR) before investing in any new project.
                        </p>
                    </div>
                </div>
            </div>

        </div>
      </div>
    </section>
  );
};